import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { guildApi } from '@/lib/guildApi'
import { describeError } from '@/lib/auth'
import { guildIconUrl } from '@/lib/avatar'
import { guildColor, guildInitials, plural } from '@/lib/guildUi'
import { cx } from '@/lib/cx'
import { useAppStore } from '@/store/app'
import { useGuildStore } from '@/store/guilds'
import styles from './InviteCard.module.css'

/**
 * Приглашение на сервер под сообщением со ссылкой klichat.ru/invite/…: значок, название, сколько
 * в сети и всего, и кнопка. Не на сервере — «Присоединиться» (вступает и открывает сервер),
 * уже там — «Перейти».
 */
export function InviteCard({ code }: { code: string }) {
  const q = useQuery({ queryKey: ['invite', code], queryFn: () => guildApi.invite(code), staleTime: 5 * 60_000, retry: false })
  const inv = q.data
  const joined = useGuildStore((s) => Boolean(inv && s.guilds[inv.guild.id]))
  const joinByCode = useGuildStore((s) => s.joinByCode)
  const openGuild = useAppStore((s) => s.openGuild)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (q.isPending) {
    return (
      <div className={styles.card} aria-busy="true" aria-label="Приглашение загружается">
        <span className={styles.label}>Приглашение на сервер</span>
        <div className={styles.guild}>
          <span className={styles.skeleton} />
          <span className={styles.skeletonText} />
        </div>
      </div>
    )
  }
  if (!inv) {
    return (
      <div className={styles.card}>
        <span className={styles.label}>Приглашение на сервер</span>
        <div className={styles.text}>
          <span className={styles.name}>Приглашение недействительно</span>
          <span className={styles.muted}>Попросите новую ссылку</span>
        </div>
      </div>
    )
  }

  const icon = guildIconUrl(inv.guild.id, inv.guild.icon)
  const go = async () => {
    if (joined) {
      openGuild(inv.guild.id)
      return
    }
    setBusy(true)
    setError(null)
    try {
      await joinByCode(code)
      openGuild(inv.guild.id)
    } catch (err) {
      setError(describeError(err).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={styles.card}>
      <span className={styles.label}>Приглашение на сервер</span>
      <div className={styles.row}>
        <div className={styles.guild}>
          <span className={styles.icon} style={icon ? undefined : { background: guildColor(inv.guild.id) }} aria-hidden="true">
            {icon ? <img className={styles.iconImage} src={icon} alt="" /> : guildInitials(inv.guild.name)}
          </span>
          <span className={styles.text}>
            <span className={styles.name} data-tooltip={inv.guild.name}>{inv.guild.name}</span>
            <span className={styles.counts}>
              <span className={styles.count}><span className={cx(styles.dot, styles.dotOnline)} aria-hidden="true" />{inv.approximate_presence_count} в сети</span>
              <span className={styles.count}><span className={styles.dot} aria-hidden="true" />{inv.approximate_member_count} {plural(inv.approximate_member_count, 'участник', 'участника', 'участников')}</span>
            </span>
          </span>
        </div>
        <button type="button" className={cx(styles.join, joined && styles.open)} disabled={busy} onClick={() => void go()}>
          {joined ? 'Перейти' : busy ? 'Вступаем…' : 'Присоединиться'}
        </button>
      </div>
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
    </div>
  )
}
